import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'framer-motion';

import { type GoodsType } from '@/common/types/goods';
import { StringSmall, StringXSmall } from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';
import { ProductionRecipe } from '@/components/Workshop/ProductionRecipe/PruductionRecipe';

import cashPng from '/img/icons/cash.png';
import cashWebp from '/img/icons/cash.webp';
import timerPng from '/img/timer.png';
import timerWebp from '/img/timer.webp';

interface OneUnitInfoProps {
  goodsName: GoodsType;
  workshopId: string;
  detailIsOpen: boolean;
  costPerUnit?: number;
  productionRate?: number;
}

const OneUnitInfo: FC<OneUnitInfoProps> = ({
  detailIsOpen,
  costPerUnit,
  productionRate,
  goodsName,
  workshopId,
}) => {
  const { t } = useTranslation();

  return (
    <AnimatePresence>
      {detailIsOpen && costPerUnit && productionRate && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="w-full flex flex-col items-center gap-4 px-6 pb-4 mt-[3.25rem]"
        >
          <div className="w-full flex gap-1">
            <div className="flex-1 bg-white/10 rounded-lg flex flex-col items-center py-2">
              <StringXSmall weight="regular" className="!text-white/40">
                {t('Sell Price per unit')}
              </StringXSmall>
              <div className="flex items-center">
                <Picture
                  className="w-8 h-8 shink-0"
                  img={cashPng}
                  webp={cashWebp}
                  alt="cash"
                />
                <StringSmall weight="bold" className="text-white">
                  {costPerUnit}
                </StringSmall>
              </div>
            </div>
            <div className="flex-1 bg-white/10 rounded-lg flex flex-col items-center py-2">
              <StringXSmall weight="regular" className="!text-white/40">
                {t('Time per Unit')}
              </StringXSmall>
              <div className="flex items-center">
                <Picture
                  className="w-8 h-8 shink-0"
                  img={timerPng}
                  webp={timerWebp}
                  alt="cash"
                />
                <StringSmall weight="bold" className="text-white">
                  {`${Math.round(3600 / productionRate)} ${t('sec.')}`}
                </StringSmall>
              </div>
            </div>
          </div>
          <StringXSmall weight="regular" className="!text-white/40 mt-1">
            {t('Required Materials')}
          </StringXSmall>
          <ProductionRecipe
            goodsName={goodsName}
            workshopId={workshopId}
            toAdd={1}
            withStock={false}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export { OneUnitInfo };
